import { StageDef } from '../automationMachineInterfaces'
import {
  InstagramRadioButtonType,
  generateRadioButtonStageDefs,
} from './instagramRadioButtonUtils'
import { generateToggleStageDefs } from './instagramToggleUtils'
import { taggingAndMentions, manuallyApproveTags } from './instagramUrls'
import { InstagramSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { InstagramRecommendationKey } from '../../utils/enums'
import {
  defaultCantUndoFn,
  getErrorIsDisabledAndReasonFn,
} from '../recommendationCardDisplayUtils'

enum WhoCanTagYouOption {
  EVERYONE = 'Allow tags from everyone',
  PEOPLE_YOU_FOLLOW = 'Allow tags from people you follow',
  NO_ONE = "Don't allow tags",
}

type OptionType = typeof WhoCanTagYouOption

type Option = WhoCanTagYouOption

const mapSettingToOptionType: Partial<Record<InstagramSettingKey, OptionType>> =
  {
    [InstagramSettingKey.WHO_CAN_TAG_YOU]: WhoCanTagYouOption,
  }

const mapSettingToTargetValue: Partial<
  Record<InstagramSettingKey, Option | boolean>
> = {
  [InstagramSettingKey.WHO_CAN_TAG_YOU]: WhoCanTagYouOption.PEOPLE_YOU_FOLLOW,
  [InstagramSettingKey.MANUALLY_APPROVE_TAGS]: true,
}

const mapSettingToSectionLabel: Partial<Record<InstagramSettingKey, string>> = {
  [InstagramSettingKey.WHO_CAN_TAG_YOU]: 'Who can tag you',
  [InstagramSettingKey.MANUALLY_APPROVE_TAGS]: 'Tag options',
}

const mapSettingToUrl: Partial<Record<InstagramSettingKey, string>> = {
  [InstagramSettingKey.WHO_CAN_TAG_YOU]: taggingAndMentions,
  [InstagramSettingKey.MANUALLY_APPROVE_TAGS]: manuallyApproveTags,
}

function getShouldUpdate(
  currentValue: Option,
  targetValue: Option,
  settingKey: string,
): boolean {
  const optionType = mapSettingToOptionType[settingKey]
  let valueMap: Partial<Record<Option, number>>
  switch (optionType) {
    case WhoCanTagYouOption:
      valueMap = {
        [WhoCanTagYouOption.EVERYONE]: 0,
        [WhoCanTagYouOption.PEOPLE_YOU_FOLLOW]: 1,
        [WhoCanTagYouOption.NO_ONE]: 2,
      }
      break
    default:
      throw new Error(`Did not recognize option type ${optionType}`)
  }
  return valueMap[currentValue] < valueMap[targetValue]
}

function getCurrentValueFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  return (initialValues) => {
    const value = initialValues?.[settingsKey]

    if (value === undefined || value === null) {
      return null
    }

    if (settingsKey === InstagramSettingKey.MANUALLY_APPROVE_TAGS) {
      return value ? 'On' : 'Off'
    }
    return value
  }
}

function getIsFulfilledFn(
  settingsKey: string,
): (initialValues: { [key: string]: any }) => any {
  const targetValue = mapSettingToTargetValue[settingsKey]

  return (initialValues) => {
    const value = initialValues?.[settingsKey]

    if (value === undefined || value === null) {
      return false
    }

    if (settingsKey === InstagramSettingKey.MANUALLY_APPROVE_TAGS) {
      return value === targetValue
    }

    return !getShouldUpdate(value, targetValue, settingsKey)
  }
}

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

enum StageKey {
  GATHER_INSTAGRAM_TAGGING = 'GATHER_INSTAGRAM_TAGGING',
  UPDATE_INSTAGRAM_TAGGING = 'UPDATE_INSTAGRAM_TAGGING',
  UNDO_INSTAGRAM_TAGGING = 'UNDO_INSTAGRAM_TAGGING',
  GATHER_INSTAGRAM_APPROVE_TAGS = 'GATHER_INSTAGRAM_APPROVE_TAGS',
  UPDATE_INSTAGRAM_APPROVE_TAGS = 'UPDATE_INSTAGRAM_APPROVE_TAGS',
  UNDO_INSTAGRAM_APPROVE_TAGS = 'UNDO_INSTAGRAM_APPROVE_TAGS',
}

const {
  gatherStageDefs: whoCanTagYouGather,
  updateStageDefs: whoCanTagYouUpdate,
  undoStageDefs: whoCanTagYouUndo,
} = generateRadioButtonStageDefs(
  InstagramRecommendationKey.INSTAGRAM_TAGGING,
  [InstagramSettingKey.WHO_CAN_TAG_YOU],
  (x) => mapSettingToSectionLabel[x],
  (x) => mapSettingToTargetValue[x],
  getShouldUpdate,
  StageKey.GATHER_INSTAGRAM_TAGGING,
  StageKey.UPDATE_INSTAGRAM_TAGGING,
  StageKey.UNDO_INSTAGRAM_TAGGING,
  mapSettingToUrl[InstagramSettingKey.WHO_CAN_TAG_YOU],
  InstagramRadioButtonType.Input,
)

const {
  gatherStageDefs: manuallyApproveTagsGather,
  updateStageDefs: manuallyApproveTagsUpdate,
  undoStageDefs: manuallyApproveTagsUndo,
} = generateToggleStageDefs(
  InstagramRecommendationKey.INSTAGRAM_TAGGING,
  [mapSettingToSectionLabel[InstagramSettingKey.MANUALLY_APPROVE_TAGS]],
  () => InstagramSettingKey.MANUALLY_APPROVE_TAGS,
  (x) => mapSettingToTargetValue[x],
  StageKey.GATHER_INSTAGRAM_APPROVE_TAGS,
  StageKey.UPDATE_INSTAGRAM_APPROVE_TAGS,
  StageKey.UNDO_INSTAGRAM_APPROVE_TAGS,
  mapSettingToUrl[InstagramSettingKey.MANUALLY_APPROVE_TAGS],
)

const gatherStageDefs: StageDef[] = [
  ...whoCanTagYouGather,
  ...manuallyApproveTagsGather,
]
const updateStageDefs: StageDef[] = [
  ...whoCanTagYouUpdate,
  ...manuallyApproveTagsUpdate,
]
const undoStageDefs: StageDef[] = [
  ...whoCanTagYouUndo,
  ...manuallyApproveTagsUndo,
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
